<script setup>
/**
 * FormSelectOptions
 * Manages an array of select picklist options dynamically.
 * Features:
 * - Drag-and-drop reordering using clean writable computed properties.
 * - Live validation to block duplicate values and texts.
 * - Autofill logic to sync 'text' strings automatically during value keystrokes.
 */
import {ref, reactive, computed, nextTick} from 'vue'
import draggable from 'vuedraggable'

const props = defineProps({
    currentData: {
        type: Object,
        required: true
    },
    field: Object
})

// --- State Layers ---

const formIsShown = ref(false)
const doAutofill = ref(true)
const errorText = ref('')
const errorValue = ref('')

const formData = reactive({
    value: '',
    text: ''
})

const addOptionBtn = ref(null)
const valueInput = ref(null)

// --- Writable Computed (The Fix) ---

/**
 * Writable Computed Property
 * Intercepts read/write actions from the template loop (and vuedraggable)
 * to stream dynamic updates directly up to the parent currentData block.
 */
const options = computed({
    get() {
        // Fallback to empty array securely if structure options are not hydrated yet
        return props.currentData?.options || []
    },
    set(newOptionsArray) {
        if (props.currentData) {
            props.currentData.options = newOptionsArray
        }
    }
})

// --- Methods ---

const onEnd = () => {
    // Vuedraggable handles internal adjustments, but this ensures a clean emit bubble up
    options.value = [...options.value]
}

const validate = () => {
    errorValue.value = ''
    errorText.value = ''

    if (options.value.length) {
        if (options.value.find(o => o.value === formData.value)) {
            errorValue.value = 'Value exists.'
        }
        if (options.value.find(o => o.text === formData.text)) {
            errorText.value = 'Text exists.'
        }
    }
}

const resetForm = () => {
    formData.value = ''
    formData.text = ''
    doAutofill.value = true
    errorValue.value = ''
    errorText.value = ''
}

const showForm = () => {
    resetForm()
    formIsShown.value = true
    nextTick(() => valueInput.value?.focus())
}

const hideForm = () => {
    formIsShown.value = false
    resetForm()
}

const autofill = () => {
    if (doAutofill.value) {
        formData.text = formData.value
    }
}

const handleEscape = (e) => {
    if (e.key === 'Escape') {
        hideForm()
        addOptionBtn.value?.focus()
    }
}

const valueInputKeyup = (event) => {
    autofill()
    handleEscape(event)
    validate()
}

const textInputKeyup = (event) => {
    doAutofill.value = false
    handleEscape(event)
    validate()
}

const addOption = () => {
    validate()
    if (errorValue.value || errorText.value) return

    // Mutate the array through the computed setter interface safely
    options.value = [...options.value, {...formData}]
    resetForm()
}

const removeOption = (index) => {
    const updatedList = [...options.value]
    updatedList.splice(index, 1)
    options.value = updatedList
}
</script>

<template>
    <div>
        <div>
            <table class="table">
                <thead>
                <tr>
                    <th style="width: 2.5rem"></th>
                    <th>Value</th>
                    <th>Text</th>
                    <th style="width: 3rem"/>
                </tr>
                </thead>

                <draggable
                    v-model="options"
                    tag="tbody"
                    handle=".handle"
                    :animation="150"
                    item-key="value"
                    @end="onEnd"
                >
                    <template #item="{ element: option, index }">
                        <tr class="visible-on-hover">
                            <td class="handle" style="cursor: grab;">⋮⋮</td>
                            <td>{{ option.value }}</td>
                            <td>{{ option.text }}</td>
                            <td>
                                <small
                                    class="bi-trash3 cursor-pointer text-danger"
                                    @click="removeOption(index)"
                                />
                            </td>
                        </tr>
                    </template>

                    <template #footer>
                        <tr v-if="!options || !options.length">
                            <td class="text-center" colspan="4">
                                <em class="text-muted">No options configured.</em>
                            </td>
                        </tr>
                    </template>
                </draggable>
            </table>
        </div>

        <div class="mt-1">
            <button
                v-show="!formIsShown"
                ref="addOptionBtn"
                class="btn btn-light btn-sm"
                type="button"
                @click="showForm"
            >
                <i class="bi-plus-lg"/> Add Option
            </button>

            <form v-if="formIsShown" class="row gy-2 gx-3 align-items-center" @submit.prevent="addOption">
                <div class="col-auto">
                    <input
                        ref="valueInput"
                        v-model="formData.value"
                        :class="{'is-invalid': errorValue}"
                        class="form-control form-control-sm"
                        placeholder="Value"
                        type="text"
                        @keyup="valueInputKeyup"
                    >
                    <div class="invalid-feedback">
                        <span>{{ errorValue || '&nbsp;' }}</span>
                    </div>
                </div>

                <div class="col-auto">
                    <input
                        v-model="formData.text"
                        :class="{'is-invalid': errorText}"
                        class="form-control form-control-sm"
                        placeholder="Text"
                        type="text"
                        @keyup="textInputKeyup"
                    >
                    <div class="invalid-feedback">
                        <span>{{ errorText || '&nbsp;' }}</span>
                    </div>
                </div>

                <div class="col-auto">
                    <button :disabled="!!errorValue || !formData.value" class="btn btn-primary btn-sm me-1"
                            type="submit">
                        Save
                    </button>
                    <button class="btn btn-light btn-sm" type="button" @click="hideForm">
                        Cancel
                    </button>
                </div>
            </form>
        </div>
    </div>
</template>

<style scoped>
.cursor-pointer {
    cursor: pointer;
}
</style>